import { __rest } from "tslib";
import { types } from "mobx-state-tree";
import { Formula } from "@concord-consortium/codap-formulas/models/formula/formula";
import { typedId } from "../../utilities/js-utils";
import { ValueType, isDate, isImageUrl, isNumeric, toNumeric } from "./data-types";
export const kDefaultFormatStr = ".3~f";
export function importValueToString(value) {
    return value == null || Number.isNaN(value) ? "" : typeof value === "string" ? value : JSON.stringify(value);
}
export const attributeTypes = [
    "categorical", "numeric", "date", "qualitative", "boundary", "checkbox", "color", "image"
];
export const Attribute = types.model("Attribute", {
    id: types.optional(types.identifier, () => typedId("ATTR")),
    clientKey: "",
    sourceID: types.maybe(types.string),
    name: types.string,
    hidden: false,
    units: "",
    formula: types.optional(Formula, () => Formula.create()),
    values: types.array(ValueType),
    title: "",
    description: types.maybe(types.string),
    // userType: types.maybe(types.enumeration([...attributeTypes])),
    precision: types.maybe(types.number),
})
    .preProcessSnapshot(snap => {
    var _a;
    // convert nulls to undefined
    const values = (_a = snap.values) === null || _a === void 0 ? void 0 : _a.map(value => value != null ? value : undefined);
    return Object.assign(Object.assign({}, snap), { values });
})
    .postProcessSnapshot(snap => {
    // Strip the id off of the formula. We don't need them.
    // We can't easily change the Formula model itself since it is coming from a library.
    const _a = snap.formula, { id } = _a, formulaWithoutId = __rest(_a, ["id"]);
    return Object.assign(Object.assign({}, snap), { formula: formulaWithoutId });
})
    .views(self => ({
    importValue(value) {
        // may eventually want to do something more sophisticated here, like convert
        // numeric values using an attribute-specific number of decimal places
        return importValueToString(value);
    },
    toNumeric(value) {
        toNumeric(value);
    },
    // CODAP3 has an optimization so that strValues and numValues are kept up to date
    // rather than (potentially) recomputing them from scratch on every change.
    get strValues() {
        return self.values.map(value => importValueToString(value));
    },
    get numValues() {
        // use toNumeric here
        return self.values.map(value => toNumeric(value));
    }
}))
    .views(self => ({
    get emptyCount() {
        return self.strValues.reduce((prev, current) => current === "" ? ++prev : prev, 0);
    },
    get numericCount() {
        return self.numValues.reduce((prev, current) => isFinite(current) ? ++prev : prev, 0);
    }
}))
    .views(self => ({
    get length() {
        return self.strValues.length;
    },
    get type() {
        // if (self.userType) return self.userType;
        if (self.numValues.length === 0)
            return;
        // only infer numeric if all non-empty values are numeric (CODAP2)
        return self.numericCount === self.numValues.length - self.emptyCount ? "numeric" : "categorical";
    },
    /**
     * A map of types to the number of values of that type.
     * If there are no values of that type there will be no entry in the map.
     * The entries in the map will be ordered by count so the first entry
     * will have the most values of that type.
     * Some values might match multiple types.
     * Every value can be considered "categorical" so that type is skipped.
     *
     * The handling of strings is going to be a bit weird. Since everything can be
     * considered categorical. The UI using these types could say if the counts
     * of other types are low enough then treat it as a string. If this logic
     * is used in more than one place, then we probably want to move it into
     * here as a view.
     *
     * It might also make sense to make this be an observable map so users
     * can observe keys. However since we can't re-order the keys after they
     * are inserted it would mean we couldn't re-order them. And it also seems
     * odd that a view would get updated like this. I think in MST these kinds
     * of observable maps would be reserved for serialized properties.
     */
    get typeCounts() {
        const counts = new Map();
        function increment(type) {
            if (!counts.has(type)) {
                counts.set(type, 1);
            }
            else {
                counts.set(type, counts.get(type) + 1);
            }
        }
        self.values.forEach(value => {
            if (isNumeric(value)) {
                increment("numeric");
            }
            if (isDate(value)) {
                increment("date");
            }
            if (isImageUrl(value)) {
                increment("image");
            }
        });
        // need to order them by count
        const entries = [...counts.entries()];
        const ordered = entries.sort((a, b) => b[1] - a[1]);
        const map = new Map(ordered);
        return map;
    },
    get includesAnyImages() {
        return self.values.some(isImageUrl);
    },
    get format() {
        return self.precision != null ? `.${self.precision}~f` : kDefaultFormatStr;
    },
    value(index) {
        return self.values[index];
    },
    strValue(index) {
        return self.strValues[index];
    },
    isNumeric(index) {
        return !isNaN(self.numValues[index]);
    },
    numValue(index) {
        return self.numValues[index];
    },
    boolean(index) {
        return ["true", "yes"].includes(self.strValues[index].toLowerCase()) ||
            (!isNaN(this.numValue(index)) ? this.numValue(index) !== 0 : false);
    },
    derive(name) {
        return { id: self.id, name: name || self.name, values: [] };
    }
}))
    .views(self => ({
    // TODO: we really need to keep track of empty values, or we need to count
    // categorical (everything not empty) but empty could be categorical
    get mostCommonType() {
        const { typeCounts } = self;
        if (typeCounts.size === 0) {
            if (self.values.length > 0) {
                // typeCounts does not include categorical
                return "categorical";
            }
            else {
                return undefined;
            }
        }
        // Extract the first element from the map which will be the type
        // with the highest count
        // If the counts of multiple types are equal then the which one
        // is returned is currently undefined
        const [[firstType, count]] = typeCounts;
        // if the identified type has more than half of the full values
        // return that, otherwise just return categorical
        const fullCount = self.values.length - self.emptyCount;
        if (count >= (fullCount / 2)) {
            return firstType;
        }
        else {
            return "categorical";
        }
    }
}))
    .actions(self => ({
    setName(newName) {
        self.name = newName;
    },
    setUnits(units) {
        self.units = units;
    },
    setDescription(description) {
        self.description = description;
    },
    // setUserType(type: AttributeType | undefined) {
    //   self.userType = type;
    // },
    // setUserFormat(precision: string) {
    //   self.userFormat = `.${precision}~f`
    // },
    setPrecision(precision) {
        self.precision = precision;
    },
    // setEditable(editable: boolean) {
    //   self.editable = editable;
    // },
    clearFormula() {
        // In CODAP the attribute formula is set to undefined when the formula is cleared
        // In CLUE the formula has been left around with undefined properties. With the use of
        // CODAP formula library it is easiest to just set the display expression to an empty string.
        self.formula.setDisplayExpression("");
    },
    setFormula(display) {
        self.formula.setDisplayExpression(display);
    },
    addValue(value, beforeIndex) {
        if ((beforeIndex != null) && (beforeIndex < self.values.length)) {
            self.values.splice(beforeIndex, 0, value);
        }
        else {
            self.values.push(value);
        }
    },
    addValues(values, beforeIndex) {
        if ((beforeIndex != null) && (beforeIndex < self.values.length)) {
            self.values.splice(beforeIndex, 0, ...values);
        }
        else {
            self.values.push(...values);
        }
    },
    setValue(index, value) {
        if ((index >= 0) && (index < self.values.length)) {
            self.values[index] = value;
        }
    },
    setValues(indices, values) {
        const length = indices.length <= values.length ? indices.length : values.length;
        for (let i = 0; i < length; ++i) {
            const index = indices[i];
            if ((index >= 0) && (index < self.values.length)) {
                self.values[index] = values[i];
            }
        }
    },
    removeValues(index, count = 1) {
        if ((index != null) && (index < self.values.length) && (count > 0)) {
            self.values.splice(index, count);
        }
    },
    // order the values of the attribute according to the provided indices
    orderValues(indices) {
        const _values = self.values.slice();
        // const _numValues = self.numValues.slice()
        for (let i = 0; i < _values.length; ++i) {
            self.values[i] = _values[indices[i]];
        }
    }
}));
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiYXR0cmlidXRlLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vLi4vc3JjL21vZGVscy9kYXRhL2F0dHJpYnV0ZS50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiO0FBQUEsT0FBTyxFQUF1QixLQUFLLEVBQUMsTUFBTSxpQkFBaUIsQ0FBQztBQUM1RCxPQUFPLEVBQUUsT0FBTyxFQUFFLE1BQU0sMkRBQTJELENBQUM7QUFDcEYsT0FBTyxFQUFFLE9BQU8sRUFBRSxNQUFNLDBCQUEwQixDQUFDO0FBQ25ELE9BQU8sRUFBYyxTQUFTLEVBQUUsTUFBTSxFQUFFLFVBQVUsRUFBRSxTQUFTLEVBQUUsU0FBUyxFQUFFLE1BQU0sY0FBYyxDQUFDO0FBRS9GLE1BQU0sQ0FBQyxNQUFNLGlCQUFpQixHQUFHLE1BQU0sQ0FBQztBQUV4QyxNQUFNLFVBQVUsbUJBQW1CLENBQUMsS0FBaUI7SUFDbkQsT0FBTyxLQUFLLElBQUksSUFBSSxJQUFJLE1BQU0sQ0FBQyxLQUFLLENBQUMsS0FBSyxDQUFDLENBQUMsQ0FBQyxDQUFDLEVBQUUsQ0FBQyxDQUFDLENBQUMsT0FBTyxLQUFLLEtBQUssUUFBUSxDQUFDLENBQUMsQ0FBQyxLQUFLLENBQUMsQ0FBQyxDQUFDLElBQUksQ0FBQyxTQUFTLENBQUMsS0FBSyxDQUFDLENBQUM7QUFDL0csQ0FBQztBQUVELE1BQU0sQ0FBQyxNQUFNLGNBQWMsR0FBRztJQUM1QixhQUFhLEVBQUUsU0FBUyxFQUFFLE1BQU0sRUFBRSxhQUFhLEVBQUUsVUFBVSxFQUFFLFVBQVUsRUFBRSxPQUFPLEVBQUUsT0FBTztDQUNqRixDQUFDO0FBR1gsTUFBTSxDQUFDLE1BQU0sU0FBUyxHQUFHLEtBQUssQ0FBQyxLQUFLLENBQUMsV0FBVyxFQUFFO0lBQ2hELEVBQUUsRUFBRSxLQUFLLENBQUMsUUFBUSxDQUFDLEtBQUssQ0FBQyxVQUFVLEVBQUUsR0FBRyxFQUFFLENBQUMsT0FBTyxDQUFDLE1BQU0sQ0FBQyxDQUFDO0lBQzNELFNBQVMsRUFBRSxFQUFFO0lBQ2IsUUFBUSxFQUFFLEtBQUssQ0FBQyxLQUFLLENBQUMsS0FBSyxDQUFDLE1BQU0sQ0FBQztJQUNuQyxJQUFJLEVBQUUsS0FBSyxDQUFDLE1BQU07SUFDbEIsTUFBTSxFQUFFLEtBQUs7SUFDYixLQUFLLEVBQUUsRUFBRTtJQUNULE9BQU8sRUFBRSxLQUFLLENBQUMsUUFBUSxDQUFDLE9BQU8sRUFBRSxHQUFHLEVBQUUsQ0FBQyxPQUFPLENBQUMsTUFBTSxFQUFFLENBQUM7SUFDeEQsTUFBTSxFQUFFLEtBQUssQ0FBQyxLQUFLLENBQUMsU0FBUyxDQUFDO0lBQzlCLEtBQUssRUFBRSxFQUFFO0lBQ1QsV0FBVyxFQUFFLEtBQUssQ0FBQyxLQUFLLENBQUMsS0FBSyxDQUFDLE1BQU0sQ0FBQztJQUN0QyxpRUFBaUU7SUFDakUsU0FBUyxFQUFFLEtBQUssQ0FBQyxLQUFLLENBQUMsS0FBSyxDQUFDLE1BQU0sQ0FBQztDQUNyQyxDQUFDO0tBQ0Qsa0JBQWtCLENBQUMsSUFBSSxDQUFDLEVBQUU7O0lBQ3pCLDZCQUE2QjtJQUM3QixNQUFNLE1BQU0sR0FBRyxNQUFBLElBQUksQ0FBQyxNQUFNLDBDQUFFLEdBQUcsQ0FBQyxLQUFLLENBQUMsRUFBRSxDQUFDLEtBQUssSUFBSSxJQUFJLENBQUMsQ0FBQyxDQUFDLEtBQUssQ0FBQyxDQUFDLENBQUMsU0FBUyxDQUFDLENBQUM7SUFDNUUsdUNBQVksSUFBSSxLQUFFLE1BQU0sSUFBRztBQUM3QixDQUFDLENBQUM7S0FDRCxtQkFBbUIsQ0FBQyxJQUFJLENBQUMsRUFBRTtJQUMxQix1REFBdUQ7SUFDdkQscUZBQXFGO0lBQ3JGLE1BQU0sS0FBOEIsSUFBSSxDQUFDLE9BQU8sRUFBMUMsRUFBRSxFQUFFLE9BQXNDLEVBQWpDLGdCQUFnQixjQUF6QixNQUEyQixDQUFlLENBQUM7SUFDakQsdUNBQVksSUFBSSxLQUFFLE9BQU8sRUFBRSxnQkFBZ0IsSUFBRztBQUNoRCxDQUFDLENBQUM7S0FDRCxLQUFLLENBQUMsSUFBSSxDQUFDLEVBQUUsQ0FBQyxDQUFDO0lBQ2QsV0FBVyxDQUFDLEtBQWlCO1FBQzNCLDRFQUE0RTtRQUM1RSxzRUFBc0U7UUFDdEUsT0FBTyxtQkFBbUIsQ0FBQyxLQUFLLENBQUMsQ0FBQztJQUNwQyxDQUFDO0lBQ0QsU0FBUyxDQUFDLEtBQWE7UUFDckIsU0FBUyxDQUFDLEtBQUssQ0FBQyxDQUFDO0lBQ25CLENBQUM7SUFDRCxpRkFBaUY7SUFDakYsMkVBQTJFO0lBQzNFLElBQUksU0FBUztRQUNYLE9BQU8sSUFBSSxDQUFDLE1BQU0sQ0FBQyxHQUFHLENBQUMsS0FBSyxDQUFDLEVBQUUsQ0FBQyxtQkFBbUIsQ0FBQyxLQUFLLENBQUMsQ0FBQyxDQUFDO0lBQzlELENBQUM7SUFDRCxJQUFJLFNBQVM7UUFDWCxxQkFBcUI7UUFDckIsT0FBTyxJQUFJLENBQUMsTUFBTSxDQUFDLEdBQUcsQ0FBQyxLQUFLLENBQUMsRUFBRSxDQUFDLFNBQVMsQ0FBQyxLQUFLLENBQUMsQ0FBQyxDQUFDO0lBQ3BELENBQUM7Q0FDRixDQUFDLENBQUM7S0FDRixLQUFLLENBQUMsSUFBSSxDQUFDLEVBQUUsQ0FBQyxDQUFDO0lBQ2QsSUFBSSxVQUFVO1FBQ1osT0FBTyxJQUFJLENBQUMsU0FBUyxDQUFDLE1BQU0sQ0FBQyxDQUFDLElBQUksRUFBRSxPQUFPLEVBQUUsRUFBRSxDQUFDLE9BQU8sS0FBSyxFQUFFLENBQUMsQ0FBQyxDQUFDLEVBQUUsSUFBSSxDQUFDLENBQUMsQ0FBQyxJQUFJLEVBQUUsQ0FBQyxDQUFDLENBQUM7SUFDckYsQ0FBQztJQUNELElBQUksWUFBWTtRQUNkLE9BQU8sSUFBSSxDQUFDLFNBQVMsQ0FBQyxNQUFNLENBQUMsQ0FBQyxJQUFJLEVBQUUsT0FBTyxFQUFFLEVBQUUsQ0FBQyxRQUFRLENBQUMsT0FBTyxDQUFDLENBQUMsQ0FBQyxDQUFDLEVBQUUsSUFBSSxDQUFDLENBQUMsQ0FBQyxJQUFJLEVBQUUsQ0FBQyxDQUFDLENBQUM7SUFDeEYsQ0FBQztDQUNGLENBQUMsQ0FBQztLQUNGLEtBQUssQ0FBQyxJQUFJLENBQUMsRUFBRSxDQUFDLENBQUM7SUFDZCxJQUFJLE1BQU07UUFDUixPQUFPLElBQUksQ0FBQyxTQUFTLENBQUMsTUFBTSxDQUFDO0lBQy9CLENBQUM7SUFDRCxJQUFJLElBQUk7UUFDTiwyQ0FBMkM7UUFDM0MsSUFBSSxJQUFJLENBQUMsU0FBUyxDQUFDLE1BQU0sS0FBSyxDQUFDO1lBQUUsT0FBTztRQUN4QyxrRUFBa0U7UUFDbEUsT0FBTyxJQUFJLENBQUMsWUFBWSxLQUFLLElBQUksQ0FBQyxTQUFTLENBQUMsTUFBTSxHQUFHLElBQUksQ0FBQyxVQUFVLENBQUMsQ0FBQyxDQUFDLFNBQVMsQ0FBQyxDQUFDLENBQUMsYUFBYSxDQUFDO0lBQ25HLENBQUM7SUFDRDs7Ozs7Ozs7Ozs7Ozs7Ozs7OztPQW1CRztJQUNILElBQUksVUFBVTtRQUNaLE1BQU0sTUFBTSxHQUFHLElBQUksR0FBRyxFQUF5QixDQUFDO1FBQ2hELFNBQVMsU0FBUyxDQUFDLElBQW1CO1lBQ3BDLElBQUcsQ0FBQyxNQUFNLENBQUMsR0FBRyxDQUFDLElBQUksQ0FBQyxFQUFFLENBQUM7Z0JBQ3JCLE1BQU0sQ0FBQyxHQUFHLENBQUMsSUFBSSxFQUFFLENBQUMsQ0FBQyxDQUFDO1lBQ3RCLENBQUM7aUJBQU0sQ0FBQztnQkFDTixNQUFNLENBQUMsR0FBRyxDQUFDLElBQUksRUFBRSxNQUFNLENBQUMsR0FBRyxDQUFDLElBQUksQ0FBRSxHQUFDLENBQUMsQ0FBQyxDQUFDO1lBQ3hDLENBQUM7UUFDSCxDQUFDO1FBQ0QsSUFBSSxDQUFDLE1BQU0sQ0FBQyxPQUFPLENBQUMsS0FBSyxDQUFDLEVBQUU7WUFDMUIsSUFBRyxTQUFTLENBQUMsS0FBSyxDQUFDLEVBQUMsQ0FBQztnQkFDbkIsU0FBUyxDQUFDLFNBQVMsQ0FBQyxDQUFDO1lBQ3ZCLENBQUM7WUFDRCxJQUFHLE1BQU0sQ0FBQyxLQUFLLENBQUMsRUFBQyxDQUFDO2dCQUNoQixTQUFTLENBQUMsTUFBTSxDQUFDLENBQUM7WUFDcEIsQ0FBQztZQUNELElBQUcsVUFBVSxDQUFDLEtBQUssQ0FBQyxFQUFDLENBQUM7Z0JBQ3BCLFNBQVMsQ0FBQyxPQUFPLENBQUMsQ0FBQztZQUNyQixDQUFDO1FBQ0gsQ0FBQyxDQUFDLENBQUM7UUFDSCw4QkFBOEI7UUFDOUIsTUFBTSxPQUFPLEdBQUcsQ0FBQyxHQUFHLE1BQU0sQ0FBQyxPQUFPLEVBQUUsQ0FBQyxDQUFDO1FBQ3RDLE1BQU0sT0FBTyxHQUFHLE9BQU8sQ0FBQyxJQUFJLENBQUMsQ0FBQyxDQUFDLEVBQUMsQ0FBQyxFQUFFLEVBQUUsQ0FBQyxDQUFDLENBQUMsQ0FBQyxDQUFDLEdBQUcsQ0FBQyxDQUFDLENBQUMsQ0FBQyxDQUFDLENBQUM7UUFDbkQsTUFBTSxHQUFHLEdBQUcsSUFBSSxHQUFHLENBQXVCLE9BQU8sQ0FBQyxDQUFDO1FBQ25ELE9BQU8sR0FBRyxDQUFDO0lBQ2IsQ0FBQztJQUNELElBQUksaUJBQWlCO1FBQ25CLE9BQU8sSUFBSSxDQUFDLE1BQU0sQ0FBQyxJQUFJLENBQUMsVUFBVSxDQUFDLENBQUM7SUFDdEMsQ0FBQztJQUNELElBQUksTUFBTTtRQUNSLE9BQU8sSUFBSSxDQUFDLFNBQVMsSUFBSSxJQUFJLENBQUMsQ0FBQyxDQUFDLElBQUksSUFBSSxDQUFDLFNBQVMsSUFBSSxDQUFDLENBQUMsQ0FBQyxpQkFBaUIsQ0FBQztJQUM3RSxDQUFDO0lBQ0QsS0FBSyxDQUFDLEtBQWE7UUFDakIsT0FBTyxJQUFJLENBQUMsTUFBTSxDQUFDLEtBQUssQ0FBQyxDQUFDO0lBQzVCLENBQUM7SUFDRCxRQUFRLENBQUMsS0FBYTtRQUNwQixPQUFPLElBQUksQ0FBQyxTQUFTLENBQUMsS0FBSyxDQUFDLENBQUM7SUFDL0IsQ0FBQztJQUNELFNBQVMsQ0FBQyxLQUFhO1FBQ3JCLE9BQU8sQ0FBQyxLQUFLLENBQUMsSUFBSSxDQUFDLFNBQVMsQ0FBQyxLQUFLLENBQUMsQ0FBQyxDQUFDO0lBQ3ZDLENBQUM7SUFDRCxRQUFRLENBQUMsS0FBYTtRQUNwQixPQUFPLElBQUksQ0FBQyxTQUFTLENBQUMsS0FBSyxDQUFDLENBQUM7SUFDL0IsQ0FBQztJQUNELE9BQU8sQ0FBQyxLQUFhO1FBQ25CLE9BQU8sQ0FBQyxNQUFNLEVBQUUsS0FBSyxDQUFDLENBQUMsUUFBUSxDQUFDLElBQUksQ0FBQyxTQUFTLENBQUMsS0FBSyxDQUFDLENBQUMsV0FBVyxFQUFFLENBQUM7WUFDNUQsQ0FBQyxDQUFDLEtBQUssQ0FBQyxJQUFJLENBQUMsUUFBUSxDQUFDLEtBQUssQ0FBQyxDQUFDLENBQUMsQ0FBQyxDQUFDLElBQUksQ0FBQyxRQUFRLENBQUMsS0FBSyxDQUFDLEtBQUssQ0FBQyxDQUFDLENBQUMsQ0FBQyxLQUFLLENBQUMsQ0FBQztJQUM5RSxDQUFDO0lBQ0QsTUFBTSxDQUFDLElBQWE7UUFDbEIsT0FBTyxFQUFFLEVBQUUsRUFBRSxJQUFJLENBQUMsRUFBRSxFQUFFLElBQUksRUFBRSxJQUFJLElBQUksSUFBSSxDQUFDLElBQUksRUFBRSxNQUFNLEVBQUUsRUFBRSxFQUFFLENBQUM7SUFDOUQsQ0FBQztDQUNGLENBQUMsQ0FBQztLQUNGLEtBQUssQ0FBQyxJQUFJLENBQUMsRUFBRSxDQUFDLENBQUM7SUFDZCwwRUFBMEU7SUFDMUUsb0VBQW9FO0lBQ3BFLElBQUksY0FBYztRQUNoQixNQUFNLEVBQUMsVUFBVSxFQUFDLEdBQUcsSUFBSSxDQUFDO1FBQzFCLElBQUksVUFBVSxDQUFDLElBQUksS0FBSyxDQUFDLEVBQUUsQ0FBQztZQUMxQixJQUFJLElBQUksQ0FBQyxNQUFNLENBQUMsTUFBTSxHQUFHLENBQUMsRUFBRSxDQUFDO2dCQUMzQiwwQ0FBMEM7Z0JBQzFDLE9BQU8sYUFBYSxDQUFDO1lBQ3ZCLENBQUM7aUJBQU0sQ0FBQztnQkFDTixPQUFPLFNBQVMsQ0FBQztZQUNuQixDQUFDO1FBQ0gsQ0FBQztRQUNELGdFQUFnRTtRQUNoRSx5QkFBeUI7UUFDekIsK0RBQStEO1FBQy9ELHFDQUFxQztRQUNyQyxNQUFNLENBQUMsQ0FBQyxTQUFTLEVBQUMsS0FBSyxDQUFDLENBQUMsR0FBRyxVQUFVLENBQUM7UUFFdkMsK0RBQStEO1FBQy9ELGlEQUFpRDtRQUNqRCxNQUFNLFNBQVMsR0FBRyxJQUFJLENBQUMsTUFBTSxDQUFDLE1BQU0sR0FBRyxJQUFJLENBQUMsVUFBVSxDQUFDO1FBQ3ZELElBQUksS0FBSyxJQUFJLENBQUMsU0FBUyxHQUFHLENBQUMsQ0FBQyxFQUFFLENBQUM7WUFDN0IsT0FBTyxTQUFTLENBQUM7UUFDbkIsQ0FBQzthQUFNLENBQUM7WUFDTixPQUFPLGFBQWEsQ0FBQztRQUN2QixDQUFDO0lBQ0gsQ0FBQztDQUNGLENBQUMsQ0FBQztLQUNGLE9BQU8sQ0FBQyxJQUFJLENBQUMsRUFBRSxDQUFDLENBQUM7SUFDaEIsT0FBTyxDQUFDLE9BQWU7UUFDckIsSUFBSSxDQUFDLElBQUksR0FBRyxPQUFPLENBQUM7SUFDdEIsQ0FBQztJQUNELFFBQVEsQ0FBQyxLQUFhO1FBQ3BCLElBQUksQ0FBQyxLQUFLLEdBQUcsS0FBSyxDQUFDO0lBQ3JCLENBQUM7SUFDRCxjQUFjLENBQUMsV0FBbUI7UUFDaEMsSUFBSSxDQUFDLFdBQVcsR0FBRyxXQUFXLENBQUM7SUFDakMsQ0FBQztJQUNELGlEQUFpRDtJQUNqRCwwQkFBMEI7SUFDMUIsS0FBSztJQUNMLHFDQUFxQztJQUNyQyx3Q0FBd0M7SUFDeEMsS0FBSztJQUNMLFlBQVksQ0FBQyxTQUFrQjtRQUM3QixJQUFJLENBQUMsU0FBUyxHQUFHLFNBQVMsQ0FBQztJQUM3QixDQUFDO0lBQ0QsbUNBQW1DO0lBQ25DLDhCQUE4QjtJQUM5QixLQUFLO0lBQ0wsWUFBWTtRQUNWLGlGQUFpRjtRQUNqRixzRkFBc0Y7UUFDdEYsNkZBQTZGO1FBQzdGLElBQUksQ0FBQyxPQUFPLENBQUMsb0JBQW9CLENBQUMsRUFBRSxDQUFDLENBQUM7SUFDeEMsQ0FBQztJQUNELFVBQVUsQ0FBQyxPQUFlO1FBQ3hCLElBQUksQ0FBQyxPQUFPLENBQUMsb0JBQW9CLENBQUMsT0FBTyxDQUFDLENBQUM7SUFDN0MsQ0FBQztJQUNELFFBQVEsQ0FBQyxLQUFpQixFQUFFLFdBQW9CO1FBQzlDLElBQUksQ0FBQyxXQUFXLElBQUksSUFBSSxDQUFDLElBQUksQ0FBQyxXQUFXLEdBQUcsSUFBSSxDQUFDLE1BQU0sQ0FBQyxNQUFNLENBQUMsRUFBRSxDQUFDO1lBQ2hFLElBQUksQ0FBQyxNQUFNLENBQUMsTUFBTSxDQUFDLFdBQVcsRUFBRSxDQUFDLEVBQUUsS0FBSyxDQUFDLENBQUM7UUFDNUMsQ0FBQzthQUNJLENBQUM7WUFDSixJQUFJLENBQUMsTUFBTSxDQUFDLElBQUksQ0FBQyxLQUFLLENBQUMsQ0FBQztRQUMxQixDQUFDO0lBQ0gsQ0FBQztJQUNELFNBQVMsQ0FBQyxNQUFvQixFQUFFLFdBQW9CO1FBQ2xELElBQUksQ0FBQyxXQUFXLElBQUksSUFBSSxDQUFDLElBQUksQ0FBQyxXQUFXLEdBQUcsSUFBSSxDQUFDLE1BQU0sQ0FBQyxNQUFNLENBQUMsRUFBRSxDQUFDO1lBQ2hFLElBQUksQ0FBQyxNQUFNLENBQUMsTUFBTSxDQUFDLFdBQVcsRUFBRSxDQUFDLEVBQUUsR0FBRyxNQUFNLENBQUMsQ0FBQztRQUNoRCxDQUFDO2FBQ0ksQ0FBQztZQUNKLElBQUksQ0FBQyxNQUFNLENBQUMsSUFBSSxDQUFDLEdBQUcsTUFBTSxDQUFDLENBQUM7UUFDaEMsQ0FBQztJQUNELENBQUM7SUFDRCxRQUFRLENBQUMsS0FBYSxFQUFFLEtBQWlCO1FBQ3ZDLElBQUksQ0FBQyxLQUFLLElBQUksQ0FBQyxDQUFDLElBQUksQ0FBQyxLQUFLLEdBQUcsSUFBSSxDQUFDLE1BQU0sQ0FBQyxNQUFNLENBQUMsRUFBRSxDQUFDO1lBQ2pELElBQUksQ0FBQyxNQUFNLENBQUMsS0FBSyxDQUFDLEdBQUcsS0FBSyxDQUFDO1FBQzdCLENBQUM7SUFDSCxDQUFDO0lBQ0QsU0FBUyxDQUFDLE9BQWlCLEVBQUUsTUFBb0I7UUFDL0MsTUFBTSxNQUFNLEdBQUcsT0FBTyxDQUFDLE1BQU0sSUFBSSxNQUFNLENBQUMsTUFBTSxDQUFDLENBQUMsQ0FBQyxPQUFPLENBQUMsTUFBTSxDQUFDLENBQUMsQ0FBQyxNQUFNLENBQUMsTUFBTSxDQUFDO1FBQ2hGLEtBQUssSUFBSSxDQUFDLEdBQUcsQ0FBQyxFQUFFLENBQUMsR0FBRyxNQUFNLEVBQUUsRUFBRSxDQUFDLEVBQUUsQ0FBQztZQUNoQyxNQUFNLEtBQUssR0FBRyxPQUFPLENBQUMsQ0FBQyxDQUFDLENBQUM7WUFDekIsSUFBSSxDQUFDLEtBQUssSUFBSSxDQUFDLENBQUMsSUFBSSxDQUFDLEtBQUssR0FBRyxJQUFJLENBQUMsTUFBTSxDQUFDLE1BQU0sQ0FBQyxFQUFFLENBQUM7Z0JBQ2pELElBQUksQ0FBQyxNQUFNLENBQUMsS0FBSyxDQUFDLEdBQUcsTUFBTSxDQUFDLENBQUMsQ0FBQyxDQUFDO1lBQ2pDLENBQUM7UUFDSCxDQUFDO0lBQ0gsQ0FBQztJQUNELFlBQVksQ0FBQyxLQUFhLEVBQUUsS0FBSyxHQUFHLENBQUM7UUFDbkMsSUFBSSxDQUFDLEtBQUssSUFBSSxJQUFJLENBQUMsSUFBSSxDQUFDLEtBQUssR0FBRyxJQUFJLENBQUMsTUFBTSxDQUFDLE1BQU0sQ0FBQyxJQUFJLENBQUMsS0FBSyxHQUFHLENBQUMsQ0FBQyxFQUFFLENBQUM7WUFDbkUsSUFBSSxDQUFDLE1BQU0sQ0FBQyxNQUFNLENBQUMsS0FBSyxFQUFFLEtBQUssQ0FBQyxDQUFDO1FBQ25DLENBQUM7SUFDSCxDQUFDO0lBQ0Qsc0VBQXNFO0lBQ3RFLFdBQVcsQ0FBQyxPQUFpQjtRQUMzQixNQUFNLE9BQU8sR0FBRyxJQUFJLENBQUMsTUFBTSxDQUFDLEtBQUssRUFBRSxDQUFDO1FBQ3BDLDRDQUE0QztRQUM1QyxLQUFLLElBQUksQ0FBQyxHQUFHLENBQUMsRUFBRSxDQUFDLEdBQUcsT0FBTyxDQUFDLE1BQU0sRUFBRSxFQUFFLENBQUMsRUFBRSxDQUFDO1lBQ3hDLElBQUksQ0FBQyxNQUFNLENBQUMsQ0FBQyxDQUFDLEdBQUcsT0FBTyxDQUFDLE9BQU8sQ0FBQyxDQUFDLENBQUMsQ0FBQyxDQUFDO1FBQ3ZDLENBQUM7SUFDSCxDQUFDO0NBQ0YsQ0FBQyxDQUFDLENBQUMifQ==